import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { ApiProvider } from "@src/store/ApiProvider";
import { withPasswordManagerRefresh } from "./passwordManagerRequest";

export interface ICreateVendorParams {
	name: string;
	url: string;
	vendorParentId?: number;
}

export interface ICreateVendorResponse {
	vendorId: number;
	name: string;
	url?: string | null;
	vendorParentId?: number | null;
	teamCode: string | null;
	crawlerId: number | null;
}

interface ICreateVendorState {
	loading: boolean;
	error: string | null;
	data: ICreateVendorResponse | null;
}

const initialState: ICreateVendorState = {
	loading: false,
	error: null,
	data: null,
};

const getAuthHeaders = () => {
	const token = localStorage.getItem("PasswordManager_accessToken") ?? "";
	return {
		Authorization: token ? `Bearer ${token}` : undefined,
		passwordManagerAccessToken: token || undefined,
		"Content-Type": "application/json",
	};
};

const buildUrl = () => {
	const baseUrl = ApiProvider.default.passwordManagerBaseURL || "https://qa.stagefront.com/password-manager/api";
	return `${baseUrl.replace(/\/$/, "")}/Vendors/with-parent`;
};

export const createVendor = createAsyncThunk<ICreateVendorResponse, ICreateVendorParams>(
	"passwordManager/createVendor",
	async ({ name, url, vendorParentId }, { rejectWithValue, dispatch }) => {
		try {
			const body = {
				name,
				url,
				...(vendorParentId !== undefined ? { vendorParentId } : {}),
			};
			const res = await withPasswordManagerRefresh(dispatch, () =>
				axios.post<ICreateVendorResponse | Array<ICreateVendorResponse>>(buildUrl(), body, { headers: getAuthHeaders() }),
			);

			const payload = res.data;
			if (Array.isArray(payload)) {
				if (!payload.length) throw new Error("Unable to parse vendor response");
				return payload[0];
			}
			if (!payload || typeof payload !== "object") {
				throw new Error("Unable to parse vendor response");
			}
			return payload;
		} catch (error: unknown) {
			if (axios.isAxiosError(error) && error.response?.status === 409) {
				return rejectWithValue("Vendor already exists");
			}
			if (axios.isAxiosError(error)) {
				const responseData = error.response?.data as { message?: string; title?: string } | string | undefined;
				const apiMessage =
					typeof responseData === "string" ? responseData : responseData?.message || responseData?.title || undefined;
				if (apiMessage) return rejectWithValue(apiMessage);
			}
			const message = error instanceof Error ? error.message : "Failed to create vendor";
			return rejectWithValue(message);
		}
	},
);

const createVendorSlice = createSlice({
	name: "passwordManager/createVendor",
	initialState,
	reducers: {},
	extraReducers: (builder) => {
		builder
			.addCase(createVendor.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(createVendor.fulfilled, (state, action) => {
				state.loading = false;
				state.error = null;
				state.data = action.payload;
			})
			.addCase(createVendor.rejected, (state, action) => {
				state.loading = false;
				state.error = (action.payload as string) || action.error.message || "Failed to create vendor";
			});
	},
});

export const createVendorReducer = createVendorSlice.reducer;
